import { Link, useParams } from 'react-router-dom'
import { useTrips } from '../hooks/useTrips'
import { useItems } from '../hooks/useItems'
import { useTripTypes } from '../hooks/useTripTypes'
import type { ItemPriority, TripStatus } from '../types'

const priorityConfig = {
  necessary: { 
    label: 'Necessary', 
    color: 'bg-green-100 text-green-800 border-green-200',
    icon: '🟢'
  },
  'good-to-have': { 
    label: 'Good to Have', 
    color: 'bg-orange-100 text-orange-800 border-orange-200',
    icon: '🟠'
  },
  comfort: { 
    label: 'Comfort', 
    color: 'bg-red-100 text-red-800 border-red-200',
    icon: '🔴'
  },
}

const statusConfig = {
  planning: { label: 'Planning', color: 'bg-blue-100 text-blue-800', icon: '📋' },
  packing: { label: 'Packing', color: 'bg-yellow-100 text-yellow-800', icon: '🎒' },
  'in-progress': { label: 'In Progress', color: 'bg-green-100 text-green-800', icon: '✈️' },
  completed: { label: 'Completed', color: 'bg-gray-100 text-gray-800', icon: '✅' },
}

export default function TripDetail() {
  const { id } = useParams<{ id: string }>()
  const { trips, tripItems, updateTripItemPacked, updateTrip, getPackingStats } = useTrips()
  const { items } = useItems()
  const { tripTypes } = useTripTypes()

  const trip = trips.find(t => t.id === id)
  const tripItemsForTrip = tripItems.filter(ti => ti.tripId === id)

  if (!trip) {
    return (
      <div className="max-w-4xl mx-auto text-center py-12">
        <h2 className="text-2xl font-semibold text-gray-900 mb-4">Trip Not Found</h2>
        <Link to="/trips" className="text-blue-600 hover:text-blue-800">
          ← Back to Trips
        </Link>
      </div>
    )
  }

  const tripType = tripTypes.find(tt => tt.id === trip.tripTypeId)
  const stats = getPackingStats(trip.id)

  const handleItemToggle = (tripItemId: string, packed: boolean) => {
    updateTripItemPacked({ id: tripItemId, packed })
  }

  const handleStatusChange = (status: TripStatus) => {
    updateTrip({ ...trip, status })
  }

  const getStatusBadge = (status: TripStatus) => {
    const config = statusConfig[status]
    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-md text-sm font-medium ${config.color}`}>
        <span>{config.icon}</span>
        {config.label}
      </span>
    )
  }

  const getPriorityBadge = (priority: ItemPriority) => {
    const config = priorityConfig[priority]
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium border ${config.color}`}>
        <span>{config.icon}</span>
        {config.label}
      </span>
    )
  }

  // Group trip items by priority
  const tripItemsWithItems = tripItemsForTrip.map(ti => {
    const item = items.find(i => i.id === ti.itemId)
    return item ? { ...ti, item } : null
  }).filter(Boolean) as (typeof tripItemsForTrip[0] & { item: typeof items[0] })[]

  const groupedItems = {
    necessary: tripItemsWithItems.filter(ti => ti.priority === 'necessary'),
    'good-to-have': tripItemsWithItems.filter(ti => ti.priority === 'good-to-have'),
    comfort: tripItemsWithItems.filter(ti => ti.priority === 'comfort'),
  }

  const getNextStatus = (currentStatus: TripStatus): TripStatus | null => {
    switch (currentStatus) {
      case 'planning': return 'packing'
      case 'packing': return stats.packed === stats.total ? 'in-progress' : null
      case 'in-progress': return 'completed'
      default: return null
    }
  }

  const nextStatus = getNextStatus(trip.status)

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <Link to="/trips" className="text-blue-600 hover:text-blue-800 text-sm mb-2 inline-block">
          ← Back to Trips
        </Link>
        <div className="flex justify-between items-start">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-3xl font-bold text-gray-900">{trip.name}</h1>
              {getStatusBadge(trip.status)}
            </div>
            {trip.description && (
              <p className="text-gray-600 mb-2">{trip.description}</p>
            )}
            <div className="text-sm text-gray-500 space-y-1">
              <p>Based on {tripType?.name || 'Unknown trip type'}</p>
              {trip.startDate && (
                <p>
                  {trip.startDate.toLocaleDateString()}
                  {trip.endDate && ` - ${trip.endDate.toLocaleDateString()}`}
                </p>
              )}
            </div>
          </div>
          {nextStatus && (
            <button
              onClick={() => handleStatusChange(nextStatus)}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              {nextStatus === 'packing' && 'Start Packing'}
              {nextStatus === 'in-progress' && 'Start Trip'}
              {nextStatus === 'completed' && 'Mark Complete'}
            </button>
          )}
        </div>
      </div>

      {/* Packing Progress */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700">Packing Progress</span>
          <span className="text-sm text-gray-600">{stats.packed}/{stats.total} items ({stats.percentage}%)</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-3">
          <div 
            className="bg-blue-600 h-3 rounded-full transition-all duration-300"
            style={{ width: `${stats.percentage}%` }}
          />
        </div>
      </div>

      {/* Items by Priority */}
      {tripItemsWithItems.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <p className="text-gray-500 text-lg mb-4">No items in this trip</p>
          <p className="text-gray-400">This trip doesn't have any items yet</p>
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedItems).map(([priority, priorityItems]) => (
            <div key={priority} className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center gap-2 mb-4">
                {getPriorityBadge(priority as ItemPriority)}
                <span className="text-gray-600">({priorityItems.length} items)</span>
              </div>
              
              {priorityItems.length === 0 ? (
                <p className="text-gray-400 text-sm">No {priorityConfig[priority as ItemPriority].label.toLowerCase()} items</p>
              ) : (
                <div className="space-y-3">
                  {priorityItems.map((tripItem) => (
                    <div
                      key={tripItem.id}
                      className={`flex items-center gap-3 p-3 border rounded-lg transition-colors ${
                        tripItem.packed 
                          ? 'bg-green-50 border-green-200' 
                          : 'border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        id={`item-${tripItem.id}`}
                        checked={tripItem.packed}
                        onChange={(e) => handleItemToggle(tripItem.id, e.target.checked)}
                        className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <div className="flex-1">
                        <label 
                          htmlFor={`item-${tripItem.id}`}
                          className={`block font-medium cursor-pointer ${
                            tripItem.packed ? 'text-green-800 line-through' : 'text-gray-900'
                          }`}
                        >
                          {tripItem.item.name}
                        </label>
                        {tripItem.item.description && (
                          <p className={`text-sm mt-1 ${
                            tripItem.packed ? 'text-green-600' : 'text-gray-600'
                          }`}>
                            {tripItem.item.description}
                          </p>
                        )}
                        {tripItem.packed && tripItem.packedAt && (
                          <p className="text-xs text-green-600 mt-1">
                            Packed {tripItem.packedAt.toLocaleString()}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}